import { matchPath } from 'react-router-dom';
import type { PublicRouteClassifierContribution } from './public-route-classifier-types.ts';

interface PublicRouteClassifierModule {
  default: PublicRouteClassifierContribution;
}

function moduleContribution(moduleValue: unknown): PublicRouteClassifierContribution {
  if (moduleValue === null || typeof moduleValue !== 'object' || Array.isArray(moduleValue)) {
    throw new TypeError('Public route classifier modules require a default contribution.');
  }
  return (moduleValue as PublicRouteClassifierModule).default;
}

export function buildPublicRouteClassifier(
  modules: Readonly<Record<string, unknown>>,
): (pathname: string) => boolean {
  const pluginIds = new Set<string>();
  const routeOwners = new Map<string, string>();

  for (const moduleValue of Object.values(modules)) {
    const classifier = moduleContribution(moduleValue);
    if (!classifier || typeof classifier.id !== 'string' || !classifier.id) {
      throw new TypeError('Public route classifiers require a plugin id.');
    }
    if (pluginIds.has(classifier.id)) {
      throw new Error(`Duplicate public route classifier plugin id "${classifier.id}"`);
    }
    pluginIds.add(classifier.id);
    if (!Array.isArray(classifier.paths)) {
      throw new TypeError(`Public route classifier "${classifier.id}" requires a paths array.`);
    }
    for (const path of classifier.paths) {
      if (typeof path !== 'string' || !path.startsWith('/')) {
        throw new TypeError(`Public route classifier "${classifier.id}" has an invalid path.`);
      }
      const owner = routeOwners.get(path);
      if (owner) {
        throw new Error(
          `Duplicate public route path "${path}" in "${owner}" and "${classifier.id}"`,
        );
      }
      routeOwners.set(path, classifier.id);
    }
  }

  const publicRoutePaths = [...routeOwners.keys()];
  return (pathname) =>
    publicRoutePaths.some((path) => matchPath({ path, end: true }, pathname) !== null);
}
